/**
 * Invite link helpers: build universal links (HTTPS) and parse invite tokens from any supported format.
 *
 * Universal links: https://yourdomain.com/invite/{token}
 * - On iOS/Android, if the app is installed the OS can open the app (requires associatedDomains / App Links).
 * - Otherwise the link opens in the browser; the web app handles /invite/{token} and redirects into the app flow.
 *
 * We still accept the legacy deeplink format (sagechef://invite/{token}) when parsing.
 */

import Constants from 'expo-constants';
import { Platform } from 'react-native';

/** AsyncStorage key for storing invite token when user opens invite link before signing in */
export const PENDING_INVITE_KEY = 'sage_chef_pending_invite_token';

const LEGACY_SCHEME_PREFIX = 'sagechef://invite/';
const INVITE_PATH = '/invite/';

function getAppUrl(): string {
  if (Platform.OS === 'web' && typeof process !== 'undefined' && process.env?.EXPO_PUBLIC_APP_URL) {
    return process.env.EXPO_PUBLIC_APP_URL.replace(/\/$/, '');
  }
  const extra = (Constants.expoConfig?.extra ?? {}) as { appUrl?: string };
  return (extra.appUrl ?? '').replace(/\/$/, '');
}

/**
 * Returns the base URL used for invite links. Prefer universal link (HTTPS) when app URL is configured.
 */
export function getInviteLinkBaseUrl(): string {
  const appUrl = getAppUrl();
  if (appUrl && (appUrl.startsWith('https://') || appUrl.startsWith('http://'))) {
    return appUrl;
  }
  return LEGACY_SCHEME_PREFIX.slice(0, -1); // 'sagechef://invite'
}

/**
 * Build the full invite link (universal link when app URL is set, otherwise legacy scheme).
 */
export function buildInviteLink(token: string): string {
  const base = getInviteLinkBaseUrl();
  if (base.startsWith('http')) {
    return `${base}${INVITE_PATH}${encodeURIComponent(token)}`;
  }
  return `${base}/${token}`;
}

/**
 * Extract invite token from user input: universal link, legacy deeplink, join-invite URL, or raw token.
 */
export function parseInviteTokenFromInput(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return '';

  if (trimmed.startsWith(LEGACY_SCHEME_PREFIX)) {
    return trimmed.slice(LEGACY_SCHEME_PREFIX.length).trim();
  }
  try {
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      const url = new URL(trimmed);
      const path = url.pathname || '';
      // /invite/{token} in path
      const idx = path.indexOf(INVITE_PATH);
      if (idx !== -1) {
        const after = path.slice(idx + INVITE_PATH.length).split('/')[0]?.split('?')[0]?.trim();
        if (after) return decodeURIComponent(after);
      }
      // /join-invite?token=... (token in query)
      const tokenFromQuery = url.searchParams.get('token');
      if (tokenFromQuery?.trim()) {
        const decoded = decodeURIComponent(tokenFromQuery.trim());
        // avoid double-decoding if token was a full URL (recurse once to get raw token)
        if (decoded.startsWith('http://') || decoded.startsWith('https://')) {
          return parseInviteTokenFromInput(decoded);
        }
        return decoded;
      }
    }
  } catch {
    // fall through to raw token
  }
  if (trimmed.includes(INVITE_PATH)) {
    const match = trimmed.match(/invite\/([^\s?/#]+)/);
    return match ? decodeURIComponent(match[1]) : trimmed;
  }
  return trimmed;
}
